/**
 * Regioni e province italiane, per i menu del modulo di iscrizione.
 *
 * Nel gestionale la provincia si salva con la sigla (PE, RM...), come fanno i
 * clienti che arrivano da Fatture in Cloud; il nome serve solo a chi compila.
 */

export interface Provincia {
  sigla: string;
  nome: string;
}

export interface Regione {
  nome: string;
  province: readonly Provincia[];
}

const p = (sigla: string, nome: string): Provincia => ({ sigla, nome });

export const REGIONI: readonly Regione[] = [
  { nome: 'ABRUZZO', province: [p('CH', 'CHIETI'), p('AQ', "L'AQUILA"), p('PE', 'PESCARA'), p('TE', 'TERAMO')] },
  { nome: 'BASILICATA', province: [p('MT', 'MATERA'), p('PZ', 'POTENZA')] },
  {
    nome: 'CALABRIA',
    province: [p('CZ', 'CATANZARO'), p('CS', 'COSENZA'), p('KR', 'CROTONE'), p('RC', 'REGGIO CALABRIA'), p('VV', 'VIBO VALENTIA')],
  },
  {
    nome: 'CAMPANIA',
    province: [p('AV', 'AVELLINO'), p('BN', 'BENEVENTO'), p('CE', 'CASERTA'), p('NA', 'NAPOLI'), p('SA', 'SALERNO')],
  },
  {
    nome: 'EMILIA-ROMAGNA',
    province: [
      p('BO', 'BOLOGNA'), p('FE', 'FERRARA'), p('FC', 'FORLÌ-CESENA'), p('MO', 'MODENA'), p('PR', 'PARMA'),
      p('PC', 'PIACENZA'), p('RA', 'RAVENNA'), p('RE', 'REGGIO EMILIA'), p('RN', 'RIMINI'),
    ],
  },
  {
    nome: 'FRIULI-VENEZIA GIULIA',
    province: [p('GO', 'GORIZIA'), p('PN', 'PORDENONE'), p('TS', 'TRIESTE'), p('UD', 'UDINE')],
  },
  {
    nome: 'LAZIO',
    province: [p('FR', 'FROSINONE'), p('LT', 'LATINA'), p('RI', 'RIETI'), p('RM', 'ROMA'), p('VT', 'VITERBO')],
  },
  { nome: 'LIGURIA', province: [p('GE', 'GENOVA'), p('IM', 'IMPERIA'), p('SP', 'LA SPEZIA'), p('SV', 'SAVONA')] },
  {
    nome: 'LOMBARDIA',
    province: [
      p('BG', 'BERGAMO'), p('BS', 'BRESCIA'), p('CO', 'COMO'), p('CR', 'CREMONA'), p('LC', 'LECCO'), p('LO', 'LODI'),
      p('MN', 'MANTOVA'), p('MI', 'MILANO'), p('MB', 'MONZA E BRIANZA'), p('PV', 'PAVIA'), p('SO', 'SONDRIO'), p('VA', 'VARESE'),
    ],
  },
  {
    nome: 'MARCHE',
    province: [p('AN', 'ANCONA'), p('AP', 'ASCOLI PICENO'), p('FM', 'FERMO'), p('MC', 'MACERATA'), p('PU', 'PESARO E URBINO')],
  },
  { nome: 'MOLISE', province: [p('CB', 'CAMPOBASSO'), p('IS', 'ISERNIA')] },
  {
    nome: 'PIEMONTE',
    province: [
      p('AL', 'ALESSANDRIA'), p('AT', 'ASTI'), p('BI', 'BIELLA'), p('CN', 'CUNEO'), p('NO', 'NOVARA'), p('TO', 'TORINO'),
      p('VB', 'VERBANO-CUSIO-OSSOLA'), p('VC', 'VERCELLI'),
    ],
  },
  {
    nome: 'PUGLIA',
    province: [
      p('BA', 'BARI'), p('BT', 'BARLETTA-ANDRIA-TRANI'), p('BR', 'BRINDISI'), p('FG', 'FOGGIA'), p('LE', 'LECCE'), p('TA', 'TARANTO'),
    ],
  },
  {
    nome: 'SARDEGNA',
    province: [p('CA', 'CAGLIARI'), p('NU', 'NUORO'), p('OR', 'ORISTANO'), p('SS', 'SASSARI'), p('SU', 'SUD SARDEGNA')],
  },
  {
    nome: 'SICILIA',
    province: [
      p('AG', 'AGRIGENTO'), p('CL', 'CALTANISSETTA'), p('CT', 'CATANIA'), p('EN', 'ENNA'), p('ME', 'MESSINA'),
      p('PA', 'PALERMO'), p('RG', 'RAGUSA'), p('SR', 'SIRACUSA'), p('TP', 'TRAPANI'),
    ],
  },
  {
    nome: 'TOSCANA',
    province: [
      p('AR', 'AREZZO'), p('FI', 'FIRENZE'), p('GR', 'GROSSETO'), p('LI', 'LIVORNO'), p('LU', 'LUCCA'),
      p('MS', 'MASSA-CARRARA'), p('PI', 'PISA'), p('PT', 'PISTOIA'), p('PO', 'PRATO'), p('SI', 'SIENA'),
    ],
  },
  { nome: 'TRENTINO-ALTO ADIGE', province: [p('BZ', 'BOLZANO'), p('TN', 'TRENTO')] },
  { nome: 'UMBRIA', province: [p('PG', 'PERUGIA'), p('TR', 'TERNI')] },
  { nome: "VALLE D'AOSTA", province: [p('AO', 'AOSTA')] },
  {
    nome: 'VENETO',
    province: [
      p('BL', 'BELLUNO'), p('PD', 'PADOVA'), p('RO', 'ROVIGO'), p('TV', 'TREVISO'), p('VE', 'VENEZIA'),
      p('VR', 'VERONA'), p('VI', 'VICENZA'),
    ],
  },
];

/** Le province di una regione; vuoto se la regione non esiste. */
export function provinceDi(regione: string): readonly Provincia[] {
  return REGIONI.find((r) => r.nome === regione)?.province ?? [];
}

/** La provincia con quella sigla dentro quella regione, se c'e'. */
export function trovaProvincia(regione: string, sigla: string): Provincia | undefined {
  return provinceDi(regione).find((pr) => pr.sigla === sigla);
}
